// Pure geometry for stacked comparison lanes (design D6).

// Per-branch vertical extents for one lane's branch stack (design D6/D7).
//
// Each branch row reserves space ABOVE its spine for any trigger pie+label that
// sits there, and BELOW for ones below — but a COLLAPSED branch only shows its
// single fork trigger, so we only reserve the side that trigger actually sits on.
// The gap between two rows = previous row's "below" extent + next row's "above"
// extent + a small visual buffer (with a minimum gap so labels never crowd the
// spine line). This is the exact gap math the single-stack `branchLayout` used —
// extracted here so each compare lane can grow its OWN branch tree with identical
// spacing (the #1 single-forecast regression guard: one lane reproduces it byte-for-byte).
// Both extents carry the same 10% the trigger labels were pulled in by (App.jsx's
// LABEL_GAP_SCALE): the room a row reserves is pie + label + channel offset, so a label
// that now sits nearer its circle needs proportionally less of it. Left unchanged, the
// rows would keep their old spacing and the tightening would only show up as extra empty
// space between branches.
const FULL_EXTENT = 90; // expanded row: pie + label + channel offset on each side
const PIE_EXTENT = 65; // collapsed row: pie only (label sits beside the pie, not above)
const ROW_BUFFER = 16; // visual breathing room
const ROW_MIN_GAP = 60; // hard floor so adjacent spines don't sit on top of each other
// Extra vertical room between branch splines, applied to both the normal spine
// and the glow spine (GlowSpine reads the same branchYs this produces). PR #1457's
// larger trigger/label fonts first bumped this to 1.15 (+15%); a later pass added
// another +15% for more breathing room between branches → 1.15 × 1.15 = 1.3225.
const BRANCH_GAP_SCALE = 1.3225;
// Anchor extent of a lane's SPINE row (the thing branches hang below). The single-stack
// layout anchored branches off a {above:100,below:100} "main forecast" extent, so we
// reuse FULL_EXTENT on both sides to keep lane-0 spacing identical.
const SPINE_EXTENT = { above: FULL_EXTENT, below: FULL_EXTENT };

function extentFor(b) {
  if (!b.collapsed) return { above: FULL_EXTENT, below: FULL_EXTENT };
  // Symmetric on purpose, even though a collapsed row's single trigger pie only ever
  // sits on ONE side (above for a "yes"/>=50% trigger, below for a "no"). Reserving
  // just that side is enough room for the pie, but it makes each gap depend on the
  // DIRECTIONS of the two rows around it: no->yes reserved 72+72 (212px), yes->yes
  // 0+72 (116px), yes->no nothing at all (floored to 60 -> 79px). One stack, three
  // different gaps, which reads as arbitrary spacing.
  //
  // Reserving the pie on BOTH sides makes every collapsed<->collapsed gap identical
  // (212px) and additionally rules out a collision the asymmetric version allowed: a
  // "no" pie hanging below one row and the "yes" pie above the next both live in the
  // SAME gap and need 72+72+buffer, but the yes->no pairing gave them only the 60px
  // floor. Costs height on yes-heavy stacks (116 -> 212 per row); evenness is worth it.
  //
  // The gap below the MAIN spine is deliberately left larger (it anchors off
  // SPINE_EXTENT's 100, giving 249px) — the spine is not a peer of the branch rows.
  return { above: PIE_EXTENT, below: PIE_EXTENT };
}

// Stack one lane's branches below `spineY`, returning { ys, bottom } where `bottom`
// is the lowest pixel the lane's last row reaches (spine center + its below extent +
// buffer) — used to place the NEXT lane's spine and to size the scroll container.
// `ys[i]` is branch i's spine Y. With no branches, ys=[] and bottom = spineY+below+buffer.
function stackLane(laneBranches, spineY) {
  const ys = [];
  let prevY = spineY;
  let prevExt = SPINE_EXTENT;
  for (const b of laneBranches || []) {
    const myExt = extentFor(b);
    const gap = Math.round(
      BRANCH_GAP_SCALE * Math.max(ROW_MIN_GAP, prevExt.below + myExt.above + ROW_BUFFER),
    );
    const y = prevY + gap;
    ys.push(y);
    prevY = y;
    prevExt = myExt;
  }
  return { ys, bottom: prevY + prevExt.below + ROW_BUFFER };
}

// Full stacked-lane geometry (design D6/D7). Each lane gets its own spine Y and its
// own branch stack hanging below it; lane N+1's spine sits below lane N's branch
// stack plus `interLaneGap` of clear separation. Produces:
//   lanes: [{ spineY, branchYs }]   per-lane spine + per-branch Ys (parallel to roots)
//   totalHeight: single height spanning ALL lanes for the scroll container
//
// SINGLE-LANE INVARIANT: with one root (compareOn=false equivalent), lanes[0].spineY
// === spineY0, lanes[0].branchYs === the old branchLayout.ys, and totalHeight ===
// the old branchLayout.totalHeight — verified because stackLane(branches, spineY0)
// runs the identical prevY/prevExt loop the old memo did.
export function stackedLaneLayout(rootsBranches, spineY0, interLaneGap) {
  const lanes = [];
  let spineY = spineY0;
  let bottom = spineY0;
  for (let i = 0; i < rootsBranches.length; i++) {
    if (i > 0) spineY = bottom + interLaneGap; // drop below previous lane's branch stack
    const { ys, bottom: laneBottom } = stackLane(rootsBranches[i], spineY);
    lanes.push({ spineY, branchYs: ys });
    bottom = laneBottom;
  }
  return { lanes, totalHeight: bottom };
}

// Order a lane's branches top→bottom by fork position, LATEST fork date first, so a
// branch that forks later in the timeline sits higher and the earliest fork sits at
// the bottom of the stack. This is the direction that keeps the fork connectors from
// bunching: each row's fork point is to the LEFT of the row above it, so the
// connectors fan out instead of crossing back over each other. (It previously ran
// earliest-first. Flipping the direction is a one-line change here, and because BOTH
// the layout and render paths order through this function, flipping it here flips
// both together.) Reorders when new branches are added — an accepted
// tradeoff for readability. DETERMINISTIC: equal fork dates tie-break on runId, so
// the layout branch list and the render branch list — derived separately
// (selectBranchRuns vs selectRunTree) but sorted with THIS same function — stay in
// lockstep. That lockstep is load-bearing: the render indexes branchYs[i] by the
// branch's array position, so any drift between the two orders would mis-place
// branch rows. Undated forks sink to the bottom. Non-mutating.
//
// A sub-branch always forks LATER than its parent (its fork is one of the parent's own
// triggers), so a flat latest-first sort put every sub-branch ABOVE its parent — and the
// connector, drawn downward from the parent row, collapsed to zero height and left the
// sub-branch floating. So the date order applies among SIBLINGS, and each branch's subtree
// is laid out directly beneath it (pre-order). The no-crossing property still holds: every
// row between a branch and its parent forks later than it, so its stream starts to the
// right of this branch's connector. A branch whose parent is not in the list (a top-level
// fork, or a parent filtered out of the lane) is laid out as top-level.
export function orderBranchesByFork(branches) {
  // null, not a numeric sentinel, for "no parseable fork date": with latest-first
  // ordering a LARGE sentinel would float undated forks to the TOP, which is the
  // opposite of where they belong. The comparator branches on null explicitly
  // instead, which also avoids ever returning NaN (undefined comparator behavior).
  const forkMs = (b) => {
    const t = b && b.forkDate ? Date.parse(b.forkDate) : NaN;
    return Number.isNaN(t) ? null : t;
  };
  const bySiblingOrder = (a, b) => {
    const fa = forkMs(a),
      fb = forkMs(b);
    // Equal values (two identical dates, or BOTH undated) fall through to the
    // stable runId tiebreak below.
    if (fa !== fb) {
      if (fa === null) return 1; // undated sinks to the bottom
      if (fb === null) return -1;
      return fa > fb ? -1 : 1; // later fork first → higher in the stack
    }
    // Code-unit comparison (not localeCompare): runIds are opaque ASCII ids and
    // we want a stable order that can't vary by the viewer's locale/collation.
    const ra = String(a?.runId ?? ""),
      rb = String(b?.runId ?? "");
    return ra < rb ? -1 : ra > rb ? 1 : 0;
  };
  const list = (branches || []).filter(Boolean);
  const ids = new Set(list.map((b) => b.runId));
  const childrenOf = new Map();
  const topLevel = [];
  for (const b of list) {
    const pid = b.parentRunId;
    if (pid != null && pid !== b.runId && ids.has(pid)) {
      if (!childrenOf.has(pid)) childrenOf.set(pid, []);
      childrenOf.get(pid).push(b);
    } else {
      topLevel.push(b);
    }
  }
  const out = [];
  const placed = new Set();
  const place = (b) => {
    if (placed.has(b)) return;
    placed.add(b);
    out.push(b);
    for (const c of (childrenOf.get(b.runId) || []).sort(bySiblingOrder)) place(c);
  };
  for (const b of topLevel.sort(bySiblingOrder)) place(b);
  // A parent cycle has no top-level entry point; never drop its rows.
  for (const b of [...list].sort(bySiblingOrder)) place(b);
  return out;
}

// Union date range across all roots' event dates and resolution dates.
// Returns { min: Date, max: Date } or null when no parseable dates exist.
export function unionDateRange(roots) {
  let min = Infinity,
    max = -Infinity,
    found = false;
  const consider = (v) => {
    const t = Date.parse(v);
    if (!Number.isNaN(t)) {
      if (t < min) min = t;
      if (t > max) max = t;
      found = true;
    }
  };
  for (const r of roots || []) {
    for (const e of r?.events || []) consider(e?.date);
    consider(r?.contract?.resolution_date);
  }
  return found ? { min: new Date(min), max: new Date(max) } : null;
}
